import {Injectable, signal} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';

/** Fallback cíl, když v in-app historii není kam se vrátit. */
const HOME_URL = '/page/dashboard';

/**
 * Vlastní in-app historie pro univerzální šipku zpět v topbaru. `window.history.back()`
 * schválně nepoužíváme — u deep-linku nebo vyčerpaného zásobníku by uživatele vyvedla
 * ÚPLNĚ MIMO aplikaci. Zásobník drží jen aplikační URL; `back()` naviguje deterministicky
 * na předchozí stránku, a když žádná není, na dashboard.
 */
@Injectable({providedIn: 'root'})
export class NavigationHistoryService {

    /** Aktuální URL — topbar podle ní schovává výchozí šipku na dashboardu. */
    readonly currentUrl = signal<string>('');

    private readonly urls: string[] = [];
    /** Právě probíhá programový krok zpět — jeho NavigationEnd se do zásobníku NEpřidává. */
    private navigatingBack = false;

    constructor(private readonly router: Router) {
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        ).subscribe(event => this.record(event.urlAfterRedirects));
        // Služba může vzniknout až po prvním NavigationEnd (topbar ji injektuje) — doseje aktuální URL.
        if (this.router.url && this.router.url !== '/') {
            this.record(this.router.url);
        }
    }

    /** Zpět na předchozí aplikační stránku; bez historie → dashboard. */
    back(): void {
        if (this.urls.length > 1) {
            this.urls.pop();
            this.navigatingBack = true;
            this.router.navigateByUrl(this.urls[this.urls.length - 1]);
        } else {
            this.router.navigateByUrl(HOME_URL);
        }
    }

    private record(url: string): void {
        this.currentUrl.set(url);
        if (this.navigatingBack) {
            this.navigatingBack = false;
            return;
        }
        // Jen aplikační stránky; po sobě jdoucí duplicity (reload, query změny téže URL) nezdvojovat.
        if (!url.startsWith('/page/')) return;
        if (this.urls[this.urls.length - 1] === url) return;
        this.urls.push(url);
        // Pojistka proti neomezenému růstu za dlouhou session.
        if (this.urls.length > 50) this.urls.shift();
    }
}
